import { Injectable } from '@angular/core';
import {
  BulkOverrideTemplateSessionDetailsResponse,
  BulkUpsertOverrideTemplateSessionRequest,
  OverrideTemplateSessionDetailsResponse,
  OverrideTemplateSessionGetListResponse,
  OverrideTemplateSessionHistoryResponse,
  OverrideTemplateSessionRunReportResponse,
  UpsertOverrideTemplateSessionPayload,
} from '@ag-common-lib/public-api';
import { LoadOptions } from 'devextreme/common/data';
import { ReportingApiService } from './reporting-api.service';

@Injectable()
export class OverrideTemplateSessionsService {
  private readonly basePath = '/api/override/session';

  constructor(private readonly reportingApiService: ReportingApiService) {}

  getList(
    sessionId: number,
    loadOptions: LoadOptions | Record<string, unknown> = {},
  ): Promise<OverrideTemplateSessionGetListResponse> {
    return this.reportingApiService.request<OverrideTemplateSessionGetListResponse>(
      `${this.basePath}/${sessionId}/template/get-list`,
      {
        method: 'POST',
        body: this.normalizeLoadOptions(loadOptions),
      },
    );
  }

  async get(sessionId: number, templateId: number): Promise<OverrideTemplateSessionDetailsResponse['data']> {
    const response = await this.reportingApiService.request<OverrideTemplateSessionDetailsResponse>(
      `${this.basePath}/${sessionId}/template/${templateId}`,
    );
    return response.data;
  }

  getHistory(
    templateId: number,
    loadOptions: LoadOptions | Record<string, unknown> = {},
  ): Promise<OverrideTemplateSessionHistoryResponse> {
    return this.reportingApiService.request<OverrideTemplateSessionHistoryResponse>(
      `${this.basePath}/template/${templateId}/get-list`,
      {
        method: 'POST',
        body: this.normalizeLoadOptions(loadOptions),
      },
    );
  }

  async upsert(
    sessionId: number,
    templateId: number,
    payload: UpsertOverrideTemplateSessionPayload,
  ): Promise<OverrideTemplateSessionDetailsResponse['data']> {
    const response = await this.reportingApiService.request<OverrideTemplateSessionDetailsResponse>(
      `${this.basePath}/${sessionId}/template/${templateId}`,
      {
        method: 'PUT',
        body: payload,
      },
    );
    return response.data;
  }

  async upsertBulk(
    sessionId: number,
    payload: BulkUpsertOverrideTemplateSessionRequest,
  ): Promise<BulkOverrideTemplateSessionDetailsResponse['data']> {
    const response = await this.reportingApiService.request<BulkOverrideTemplateSessionDetailsResponse>(
      `${this.basePath}/${sessionId}/template/bulk`,
      {
        method: 'PUT',
        body: payload,
      },
    );
    return response.data;
  }

  delete(sessionId: number, templateId: number): Promise<void> {
    return this.reportingApiService.request<void>(`${this.basePath}/${sessionId}/template/${templateId}`, {
      method: 'DELETE',
    });
  }

  async run(sessionId: number, templateId: number): Promise<OverrideTemplateSessionRunReportResponse['data']> {
    const response = await this.reportingApiService.request<OverrideTemplateSessionRunReportResponse>(
      `${this.basePath}/${sessionId}/template/${templateId}/run`,
      {
        method: 'POST',
      },
    );
    return response.data;
  }

  getPdf(sessionId: number, templateId: number): Promise<Blob> {
    return this.reportingApiService.request<Blob>(`${this.basePath}/${sessionId}/template/${templateId}/pdf`, {
      method: 'POST',
      responseType: 'blob',
    });
  }

  async confirm(sessionId: number, templateId: number): Promise<OverrideTemplateSessionDetailsResponse['data']> {
    const response = await this.reportingApiService.request<OverrideTemplateSessionDetailsResponse>(
      `${this.basePath}/${sessionId}/template/${templateId}/confirm`,
      {
        method: 'POST',
      },
    );
    return response.data;
  }

  private normalizeLoadOptions(
    loadOptions: LoadOptions | Record<string, unknown> | null | undefined,
  ): Record<string, unknown> {
    const options = loadOptions && typeof loadOptions === 'object' ? (loadOptions as Record<string, unknown>) : {};
    const sort =
      Array.isArray(options.sort) && options.sort.length
        ? options.sort
        : [
            {
              selector: 'templateId',
              desc: false,
            },
          ];

    return {
      skip: typeof options.skip === 'number' ? options.skip : 0,
      take: typeof options.take === 'number' ? options.take : 10,
      sort,
      filter: options.filter ?? [],
    };
  }
}
